import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useHoverIntent } from "./useHoverIntent.js";

// One controller per chart: point changes do not restart the entry timer.
// Keep visual highlighting separate from this explanation-only state.
export function useChartHoverIntent(resetKey) {
  const intent = useHoverIntent();
  const started = useRef(false);
  const keyboard = useRef(false);
  const dismissed = useRef(false);
  const [point, setPoint] = useState(null);
  const reset = useCallback(() => {
    started.current = false;
    keyboard.current = false;
    dismissed.current = false;
    setPoint(null);
    intent.reset();
  }, [intent.reset]);
  useLayoutEffect(() => { reset(); }, [resetKey, reset]);

  const activate = useCallback((next) => {
    if (dismissed.current) return;
    setPoint(next);
    if (next == null) {
      // A margin is not a valid initial target. After opening, gaps may hide
      // content without ending the chart session.
      if (!intent.open) {
        started.current = false;
        intent.dismiss();
      }
      return;
    }
    if (keyboard.current) intent.show();
    else if (!started.current) {
      started.current = true;
      intent.enter();
    }
  }, [intent.open, intent.dismiss, intent.enter, intent.show]);
  const pointProps = (value) => ({
    tabIndex: 0,
    "data-chart-hover-point": intent.triggerProps["data-hover-intent-trigger"],
    onMouseEnter: () => activate(value),
    onMouseLeave: (event) => {
      const next = event.relatedTarget?.closest?.("[data-chart-hover-point]");
      if (next?.getAttribute("data-chart-hover-point") === intent.triggerProps["data-hover-intent-trigger"]) return;
      activate(null);
    },
    onFocus: (event) => {
      dismissed.current = false;
      intent.triggerProps.onFocus(event);
      activate(value);
    },
    onBlur: (event) => {
      if (!event.currentTarget.parentElement?.contains(event.relatedTarget)) reset();
    },
  });
  return {
    open: intent.open && point != null,
    point,
    activate,
    pointProps,
    reset,
    containerProps: {
      ...intent.triggerProps,
      onMouseEnter: undefined,
      onMouseLeave: reset,
      onPointerDown: () => { keyboard.current = false; intent.triggerProps.onPointerDown(); },
      onMouseDown: () => { keyboard.current = false; intent.triggerProps.onMouseDown(); },
      onFocus: (event) => {
        intent.triggerProps.onFocus(event);
      },
      onBlur: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) reset();
      },
      onKeyDownCapture: (event) => {
        if (event.key !== "Escape") {
          dismissed.current = false;
          keyboard.current = true;
          intent.triggerProps.onKeyDown(event);
          if (point != null) intent.show();
        }
      },
      onKeyDown: (event) => {
        intent.triggerProps.onKeyDown(event);
        if (event.key === "Escape") {
          started.current = true; // Stay dismissed until a true exit/re-entry.
          dismissed.current = true;
          setPoint(null);
        } else {
          keyboard.current = true;
          if (point != null) intent.show();
        }
      },
    },
    chartProps: {
      accessibilityLayer: true,
      onMouseEnter: (state) => activate(
        state?.isTooltipActive && state.activePayload?.some((entry) => entry.value != null) ? state.activePayload : null,
      ),
      onMouseMove: (state) => activate(
        state?.isTooltipActive && state.activePayload?.some((entry) => entry.value != null) ? state.activePayload : null,
      ),
    },
    tooltipProps: {
      active: intent.open && point != null,
      // Recharts still owns position/cursor; suppress even its empty wrapper.
      wrapperStyle: { visibility: intent.open && point != null ? "visible" : "hidden" },
    },
  };
}
